/**
 * Turning spreadsheet strings into facts.
 *
 * THIS FILE IS THE HARD RULE'S BOUNDARY. Everything upstream of it is a customer's export;
 * everything downstream trusts what comes out. The catalog's three-valued logic is only as
 * honest as this conversion: the moment a blank cell becomes `false` instead of `undefined`,
 * an obligation silently disappears and a SKU turns green. Every function here fails towards
 * `undefined` — "we were not told" — and never towards a decision.
 *
 * See tabeen_AGENTS.md "The hard rule" and packages/catalog/src/facts.ts.
 */

export type FactValue = string | number | boolean | null;

export interface Coerced {
  /** `undefined` means unknown. `null` means the sheet positively said "none". */
  readonly value: FactValue | undefined;
  readonly warning?: string;
}

const UNKNOWN: Coerced = { value: undefined };

/** Spellings that mean "the seller answered, and the answer is nothing". */
const EXPLICIT_NONE = new Set(["none", "n/a", "na", "not applicable", "-", "—", "keine", "aucun"]);

const TRUTHY = new Set(["yes", "y", "true", "t", "1", "x", "ja", "oui", "si", "sì"]);
const FALSY = new Set(["no", "n", "false", "f", "0", "nein", "non"]);

/**
 * Text. A blank cell is unknown, not an empty string: "" would satisfy a required-data check
 * that is really still outstanding.
 */
export function coerceText(raw: string): Coerced {
  const value = raw.trim();
  if (value === "") return UNKNOWN;
  if (EXPLICIT_NONE.has(value.toLowerCase())) return { value: null };
  return { value };
}

/**
 * Booleans, the most dangerous column type in the file.
 *
 * A `has_battery` column with a blank cell is the classic case: the seller filled in the rows
 * that do have batteries and left the rest empty, meaning "no" — or they exported from a system
 * that has no such field at all, meaning "no idea". Those are indistinguishable from here, so
 * both become unknown and the customer gets asked. Guessing "no" would be right most of the
 * time, which is exactly what makes it dangerous: the failures are rare, silent, and land on
 * the SKUs that most needed the requirement.
 */
export function coerceBoolean(raw: string): Coerced {
  const value = raw.trim().toLowerCase();
  if (value === "") return UNKNOWN;
  if (TRUTHY.has(value)) return { value: true };
  if (FALSY.has(value)) return { value: false };
  // "-", "n/a", "none" in a yes/no column is a placeholder, not an answer: the seller may mean
  // "no battery" or "not filled in", and this file cannot tell which. Until 17 October 2026 it
  // read as "no", which made a battery or toy obligation disappear with no warning (D-015).
  if (EXPLICIT_NONE.has(value)) {
    return {
      value: undefined,
      warning: `"${raw.trim()}" is a placeholder, not a yes/no answer, so it was read as unknown. Write yes or no.`,
    };
  }
  return {
    value: undefined,
    warning: `"${raw.trim()}" is not a yes/no value, so it was read as unknown rather than guessed.`,
  };
}

/**
 * ISO 3166-1 alpha-2 country codes, plus the English, German and French names most likely to
 * appear in a seller's export. Not exhaustive by design — an unrecognised value becomes
 * unknown with a warning rather than being passed through, because the catalog derives EU
 * membership from this and "Made in PRC" would silently derive as non-EU. Right by accident is
 * still a bug.
 */
const COUNTRY_NAMES: Readonly<Record<string, string>> = {
  china: "CN", "pr china": "CN", prc: "CN", "peoples republic of china": "CN",
  germany: "DE", deutschland: "DE", allemagne: "DE",
  france: "FR", frankreich: "FR",
  italy: "IT", italia: "IT", italien: "IT",
  spain: "ES", espana: "ES", "españa": "ES", spanien: "ES",
  netherlands: "NL", nederland: "NL", holland: "NL", niederlande: "NL",
  belgium: "BE", belgie: "BE", belgique: "BE",
  poland: "PL", polska: "PL",
  austria: "AT", osterreich: "AT", "österreich": "AT",
  ireland: "IE", portugal: "PT", sweden: "SE", denmark: "DK", finland: "FI",
  czechia: "CZ", "czech republic": "CZ", romania: "RO", hungary: "HU", greece: "GR",
  "united kingdom": "GB", uk: "GB", "great britain": "GB", england: "GB", britain: "GB",
  "united states": "US", usa: "US", "united states of america": "US",
  vietnam: "VN", "viet nam": "VN", india: "IN", turkey: "TR", "türkiye": "TR", turkiye: "TR",
  taiwan: "TW", "hong kong": "HK", japan: "JP", "south korea": "KR", korea: "KR",
  thailand: "TH", indonesia: "ID", malaysia: "MY", bangladesh: "BD", pakistan: "PK",
  cambodia: "KH", mexico: "MX", canada: "CA", switzerland: "CH", norway: "NO",
};

/**
 * Every assigned ISO 3166-1 alpha-2 code, plus XK (Kosovo: user-assigned, but what EU systems use).
 * Two letters used to pass straight through, so "UK" stayed "UK" — not GB, so a British seller
 * was never "established in GB" — "EU" became a country and "No" became Norway (found in review,
 * 17 October 2026). test/values.test.ts checks every code here against the runtime's own region
 * names, which catches a typo in this list.
 */
export const ISO_ALPHA2: ReadonlySet<string> = new Set(
  (
    "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS " +
    "BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE " +
    "EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM " +
    "HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC " +
    "LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA " +
    "NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW " +
    "SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO " +
    "TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW XK"
  ).split(" "),
);

/** Two-letter spellings in common use that are not the ISO code: the UK's, and the EU's own for Greece. */
const COUNTRY_CODE_ALIASES: Readonly<Record<string, string>> = { UK: "GB", EL: "GR" };

export function coerceCountry(raw: string): Coerced {
  const value = raw.trim();
  if (value === "") return UNKNOWN;
  if (EXPLICIT_NONE.has(value.toLowerCase())) return { value: null };

  if (/^[A-Za-z]{2}$/.test(value)) {
    const upper = value.toUpperCase();
    const alias = COUNTRY_CODE_ALIASES[upper];
    if (alias) return { value: alias };
    // "No" in a country column is an answer to a different question, not Norway. Only the
    // upper-case code is read as one.
    if (value !== upper && FALSY.has(value.toLowerCase())) {
      return { value: undefined, warning: `"${value}" is not a country, so it was read as unknown. For Norway write NO.` };
    }
    if (ISO_ALPHA2.has(upper)) return { value: upper };
    return {
      value: undefined,
      warning: `"${value}" is not an ISO 3166-1 country code, so it was read as unknown. Use a code such as CN or DE.`,
    };
  }

  const normalised = value.toLowerCase().replace(/[.,]/g, "").replace(/\s+/g, " ").trim();
  const code = COUNTRY_NAMES[normalised];
  if (code) return { value: code };

  return {
    value: undefined,
    warning: `"${value}" was not recognised as a country, so it was read as unknown. Use an ISO code such as CN or DE.`,
  };
}

/**
 * Digits only, length- and check-digit-checked. A malformed barcode is worse than an absent one.
 * The last digit of every GTIN is a GS1 check digit, so a barcode typed with one digit wrong, or
 * two swapped, fails it. Until 24 October 2026 only the length was checked, and such a barcode was
 * kept as the product's identifier.
 */
export function coerceGtin(raw: string): Coerced {
  const value = raw.trim().replace(/[\s-]/g, "");
  if (value === "") return UNKNOWN;
  if (EXPLICIT_NONE.has(value.toLowerCase())) return { value: null };
  if (!/^\d{8}$|^\d{12,14}$/.test(value)) {
    return {
      value: undefined,
      warning: `"${raw.trim()}" is not a valid GTIN/EAN/UPC length (8, 12, 13 or 14 digits).`,
    };
  }
  if (!hasValidCheckDigit(value)) {
    return {
      value: undefined,
      warning: `"${raw.trim()}" has the wrong check digit for a GTIN/EAN/UPC, so it was read as unknown. Check it for a typo.`,
    };
  }
  return { value };
}

/** GS1's mod-10 check: from the right, the digits before the check digit are weighted 3, 1, 3, 1… */
function hasValidCheckDigit(digits: string): boolean {
  const values = [...digits].map(Number);
  const check = values.pop();
  const sum = values.reverse().reduce((total, digit, i) => total + digit * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 === check;
}
